import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import moment from "moment";
import * as React from "react";
import { DriversRow, WeeklyReportsRow } from "../../api";
import { WeekPicker } from "../../components/datepickers/WeekPicker";
import { DriverPulldown } from "../../components/driverPulldown";
import { OfficePullDown } from "../../components/officePullDown";
import { HermesSnackbar } from "../../components/snackbar";
import {
    HeadCell,
    SortableTable,
    newDisplayCond,
} from "../../components/table/sortableTable";
import { formatPaymentAmountDisplay } from "../../services/common/convert";
import * as service from "../../services/weekly/weeklyreport";
import { commonState } from "../../store";
import { colors, fontSizes } from "../../theme";
import { ShiftsModal } from "./DetailScreen/DetailModal";
import { DetailReportState } from "./DetailScreen/store";
import { WeeklyReportState } from "./store";

import {
    LoadingTableElement,
    NullTableElement,
} from "../../components/table/nullTable";

// TODO: clean up props
interface Props {
    common: commonState;
    weeklyreport: WeeklyReportState;
    weeklydetail: DetailReportState;
}

const SearchBar: React.FC<Props> = (props: Props) => {
    const onSelectDriver = (driver: DriversRow) => {
        service.selectWeeklyFilterDriver(driver);
    };

    const onClearDriver = () => {
        service.selectWeeklyFilterDriver(null);
    };

    const onSelectOffice = (office_id: number | null) => {
        service.selectWeeklyReportsOfficeId(office_id);
        service.selectWeeklyReports();
    };

    const set_start_date = (ev: any) => {
        service.selectStartDate(ev as string);
    };

    const koekakeLabel = { inputProps: { "aria-label": "未声かけのみ表示" } };

    return (
        <Box sx={{ pb: 6 }}>
            <Box
                id="calendarOfficeDriverSelectors"
                sx={{
                    flexDirection: "row",
                    display: "flex",
                    width: "90%",
                    justifyContent: "space-around",
                    alignItems: "end",
                }}
            >
                <Box>
                    <WeekPicker
                        onChangeMethod={(ev) => {
                            const out = moment(ev).format("YYYY-MM-DD");
                            set_start_date(out);
                            service.selectWeeklyReports();
                        }}
                        initialDate={props.weeklyreport.selectedStartDate}
                    />
                </Box>
                <Box>
                    <OfficePullDown
                        useAllOfficeIds={false}
                        selectedOfficeId={props.weeklyreport.selectedOfficeId}
                        onChange={onSelectOffice}
                    />
                </Box>
                <Box>
                    <DriverPulldown
                        selectedDriver={props.weeklyreport.selectedDriver}
                        onChange={onSelectDriver}
                        onClear={onClearDriver}
                        driverCompleteData={props.common.driverNames}
                        initWithNull={false}
                    />
                </Box>
                <Box>
                    <label style={{ color: colors.black.main }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box>未声かけのみ表示</Box>
                            <Switch
                                {...koekakeLabel}
                                checked={
                                    props.weeklyreport.viewingUnFeedbackedOnly
                                }
                                onClick={(ev) => {
                                    handleNotKoekakeOnlySwitch(ev);
                                }}
                            />
                        </Box>
                    </label>
                </Box>
            </Box>
        </Box>
    );
};

const handleNotKoekakeOnlySwitch = (ev: any) => {
    service.setViewingNonFeedbacked(ev.target.checked);
    service.selectWeeklyReports(ev.target.checked);
};

const DriverCodeCell: React.FC<Row> = (props: Row) => {
    return (
        <Box sx={{ p: 2, pl: 0 }}>
            <Box>
                <Box sx={{ fontSize: fontSizes.small }}>
                    {props.driver_code}
                </Box>
                <Box>{props.driver_name}</Box>
            </Box>
        </Box>
    );
};
interface KoekakeProps {
    driver_id: number;
    feedback_flag: boolean;
    office_id: number;
    week_no: number;
    year: number;
}

const KoekakeCell: React.FC<KoekakeProps> = (props: KoekakeProps) => {
    const label = { inputProps: { "aria-label": "声かけ" } };
    return (
        <Box>
            <label style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                    defaultChecked={props.feedback_flag}
                    sx={{
                        "& .MuiSvgIcon-root": { fontSize: fontSizes.xlarge },
                    }}
                    {...label}
                    onChange={(x) => {
                        service.driverCellSnackbarAction(x.target.checked);
                        service.setKoeKakeFlag(
                            x.target.checked,
                            props.office_id,
                            props.driver_id,
                            props.week_no,
                            props.year
                        );
                    }}
                />
                <Box>声かけ</Box>
            </label>
        </Box>
    );
};

export const filterByDriver = (
    reports: WeeklyReportsRow[],
    driverId: number | null | undefined
): WeeklyReportsRow[] => {
    let filtered = reports;
    if (driverId) {
        filtered = reports.filter((x: WeeklyReportsRow) => {
            return x.driverId == driverId;
        });
    }
    return filtered;
};
interface WeeklyTableProps extends Props {
    onClickWeekly: any;
}

const WeeklySummaryTable: React.FC<WeeklyTableProps> = (
    props: WeeklyTableProps
) => {
    if (!props.weeklyreport.weeklyReports) {
        return <LoadingTableElement />;
    }

    const handleRowClick = (r) => {
        service.selectDetailScreenDriver(
            r.driver_id,
            r.driver_code,
            r.driver_name
        );
        service.selectDriverDetailHeaderInfo(
            r.comment,
            r.avg_revenue,
            r.office_avg_revenue,
            r.shift_nr,
            r.worst_shift_id,
            r.feedback_flag,
            r.week_no,
            r.year
        );
        service.selectShiftId(r.worst_shift_id);
        service.setExpandedContainerName(r.worst_shift_id);
        service.getShiftsPerDriver();
        props.onClickWeekly();
        window.history.pushState("modal", "Modal", "/weekly_driver_shift");
    };

    if (props.weeklyreport.weeklyReports.length == 0) {
        return <NullTableElement />;
    }

    const rows = convertData(
        props.weeklyreport.weeklyReports,
        props.weeklyreport.selectedDriver
    );

    if (!rows) {
        return <NullTableElement />;
    }

    return (
        <SortableTable
            tableHeaders={headCells}
            tableData={rows}
            tableProps={props}
            displayCond={newDisplayCond("", "asc", 0, 10)}
            rowIdField={"driver_code"}
            onRowClickMethod={handleRowClick}
            slimRows={true}
        />
    );
};

interface State {
    modalOpen: boolean;
}

export class WeeklyReportsContainer extends React.Component<Props, State> {
    constructor(props: any) {
        super(props);
        this.state = {
            modalOpen: false,
        };
    }

    componentDidMount = (): void => {
        service.clearNeighborsOn();
    };

    onClick = (): void => {
        this.setState({ modalOpen: true });
    };

    onCloseModal = (): void => {
        this.setState({ modalOpen: false });
    };

    render = () => {
        if (this.props.weeklyreport.needUpdateFlag) {
            service.selectWeeklyReports();
            service.setNeedUpdateFlag(false);
        }

        return (
            <Box>
                <SearchBar {...this.props} />
                <WeeklySummaryTable
                    {...this.props}
                    onClickWeekly={this.onClick}
                />
                <ShiftsModal
                    open={this.state.modalOpen}
                    setClose={this.onCloseModal}
                    common={this.props.common}
                    weeklyreport={this.props.weeklyreport}
                    weeklydetail={this.props.weeklydetail}
                />
                <HermesSnackbar
                    open={this.props.common.snackbarToggled}
                    msg={this.props.common.snackbarMsg}
                />
            </Box>
        );
    };
}

const convertData = (
    reports: WeeklyReportsRow[] | null,
    driver: DriversRow | null
): Row[] => {
    let filteredReports: any = reports;
    if (driver && reports) {
        filteredReports = filterByDriver(reports, driver.driverId);
    }
    const _data = filteredReports.map((rec: WeeklyReportsRow) => {
        return {
            driver_code: rec.driverCode,
            driver_name: rec.driverName,
            driver_id: rec.driverId,
            comment: rec.instructionMessage,
            comment_priority: rec.instructionPriority,
            shift_nr: rec.numShift,
            avg_revenue: rec.meanTotalAmountPerShift,
            office_avg_revenue: rec.meanOfficeTotalAmountPerShift,
            office_id: rec.officeId,
            worst_shift_id: rec.worstShiftId,
            feedback_flag: rec.feedbackGivenFlag,
            week_no: rec.weekNo,
            year: rec.year,
        } as Row;
    });

    return _data;
};

const headCells: HeadCell[] = [
    {
        label: "乗務員",
        id: "driver_code",
        grow: 2,
        wrap: true,
        numeric: false,
        narrowRow: true,
        cell: function DC(r: Row): JSX.Element {
            return <DriverCodeCell {...r} />;
        },
    },
    {
        label: "改善コメント",
        id: "comment_priority",
        numeric: false,
        grow: 4,
        wrap: true,
        align: "right",
        displayField: "comment",
        narrowRow: true,
        format: (x: number): string => {
            return `${x}`;
        },
    },
    {
        label: "期間乗務回数",
        id: "shift_nr",
        grow: 1,
        wrap: true,
        align: "right",
        headerAlignRight: true,
        numeric: true,
        narrowRow: true,
        format: (x: number): string => {
            return `${x}回`;
        },
    },
    {
        label: "平均営収",
        id: "avg_revenue",
        numeric: true,
        grow: 2,
        align: "right",
        headerAlignRight: true,
        wrap: true,
        narrowRow: true,
        format: (x: number): string => {
            return formatPaymentAmountDisplay(x, true);
        },
    },
    {
        label: "営業所平均営収",
        id: "office_avg_revenue",
        numeric: true,
        grow: 2,
        align: "right",
        headerAlignRight: true,
        wrap: true,
        narrowRow: true,
        format: (x: number): string => {
            return formatPaymentAmountDisplay(x, true);
        },
    },
    {
        label: "",
        id: "feedback_flag",
        numeric: true,
        grow: 2,
        align: "right",
        wrap: true,
        narrowRow: true,
        headerAlignRight: true,
        unClickable: true,
        cell: function DC(r: Row): JSX.Element {
            return (
                <KoekakeCell
                    driver_id={r.driver_id}
                    feedback_flag={r.feedback_flag}
                    office_id={r.office_id}
                    week_no={r.week_no}
                    year={r.year}
                />
            );
        },
    },
];

type Row = {
    driver_name: string;
    driver_code: string;
    driver_id: number;
    comment: string;
    comment_priority: number;
    shift_nr: number;
    avg_revenue: number;
    office_avg_revenue: number;
    office_id: number;
    worst_shift_id: string;
    feedback_flag: boolean;
    week_no: number;
    year: number;
};
